// Product events: what people do in the app that never writes to the database
// (opening feedback, viewing a rubric, the tour, time on a page). Events go to
// the backend (POST /app-events/batch), which validates them again, stamps the
// user id and role from the JWT and publishes them to Pub/Sub → BigQuery
// (app_events.client_events). Design and event list:
// docs/product-analytics-events.md.
//
// Plain module, no React: call sites only ever call track() / trackOnce().
// Nothing here may throw into the app or touch the session: responses,
// including 401s, are ignored. Off unless the build sets
// REACT_APP_PRODUCT_EVENTS=on.

import { matchPath } from 'react-router-dom';
import { getAuthToken } from '../userLocalDetails';
import { isEmbeddedMode } from './ltiEmbedded';
import {
  ROUTE_PATTERNS,
  isRouteTemplate,
  isUuid,
  validateProps,
} from './productEvents.schema';

export { ROUTE_PATTERNS };

const ENDPOINT_PATH = '/app-events/batch';
const MAX_BATCH = 25;
const FLUSH_AT = 10;
const FLUSH_INTERVAL_MS = 15_000;
const MAX_QUEUE = 50;
const IDLE_MS = 5 * 60_000;
const MAX_ENGAGED_MS = 30 * 60_000;
const ACTIVITY_THROTTLE_MS = 5_000;
const SID_KEY = 'productEvents.sid';

// Pages opened with a credential in the URL, or by people who are not signed
// in: nothing is recorded while one is showing.
const TOKEN_ROUTE_PATTERNS = [
  '/check/verify',
  '/check/:token',
  '/thanks/:code',
  '/gift/verify',
  '/view/submissions/:id',
  '/m/upload/:token',
  // Exams public report pages (the link's token is the credential).
  '/r/:token',
  '/sr/:token',
];

const isEnabled = () => process.env.REACT_APP_PRODUCT_EVENTS === 'on';

const apiBaseUrl = () =>
  (process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080').replace(
    /\/+$/,
    ''
  );

const buildSha = () => {
  const sha = (process.env.REACT_APP_BUILD_SHA ?? '').toLowerCase();
  return /^[a-f0-9]{7,40}$/.test(sha) ? sha : 'dev';
};

// Parcel builds set NODE_ENV=production; REACT_APP_ENV is not set on Netlify.
const warn = (...args) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[productEvents]', ...args);
  }
};

const uuid = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

/** Path of the current hash route, without its query string. */
const currentPath = () => {
  const hash = (window.location.hash || '').replace(/^#/, '');
  const path = hash.split('?')[0] || '/';
  return path.startsWith('/') ? path : `/${path}`;
};

const firstMatch = (patterns, path) =>
  patterns.find((pattern) => matchPath(path, { path: pattern, exact: true }));

/** The route template for a path ('/tasks/:assignmentId'), or 'other'. */
export const routeTemplate = (path = currentPath()) => {
  const template = firstMatch(ROUTE_PATTERNS, path);
  return template && isRouteTemplate(template) ? template : 'other';
};

const isTokenRoute = (path = currentPath()) =>
  Boolean(firstMatch(TOKEN_ROUTE_PATTERNS, path));

// The signed-in user's id as the app stores it (null when signed out). Only
// compared, never sent: the server stamps the user from the JWT.
const currentOwner = () => {
  try {
    return localStorage.getItem('userId');
  } catch {
    return null;
  }
};

const isSignedIn = () => currentOwner() !== null;

const deviceClass = () => {
  try {
    const width = window.innerWidth || 0;
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches === true;
    if (coarse && width < 768) return 'phone';
    if (coarse && width < 1367) return 'tablet';
  } catch {
    // fall through
  }
  return 'desktop';
};

const readSession = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SID_KEY) || 'null');
  } catch {
    return state.memorySession;
  }
};

const writeSession = (session) => {
  state.memorySession = session;
  try {
    sessionStorage.setItem(SID_KEY, JSON.stringify(session));
  } catch {
    // kept in memory only
  }
};

/**
 * The tab's session id and the next sequence number. A different person
 * signing in on the same tab (a shared classroom device) starts a new session,
 * so `seq` counts from 0 within each sid and its gaps measure loss.
 */
const nextInSession = (owner) => {
  const stored = readSession();
  const session =
    stored &&
    stored.owner === owner &&
    isUuid(stored.sid) &&
    Number.isInteger(stored.seq)
      ? stored
      : { sid: uuid(), owner, seq: 0 };
  writeSession({ ...session, seq: session.seq + 1 });
  return { sid: session.sid, seq: session.seq };
};

/**
 * When the signed-in user changes, nothing recorded for the previous one (or
 * while signed out) may go out under the new user's JWT: drop the queue and
 * the once-per-tab keys, and restart engaged time.
 */
const syncOwner = () => {
  const owner = currentOwner();
  if (owner === state.owner) return owner;
  if (state.ownerSeen) {
    state.queue = [];
    state.sent = new Set();
    state.engagedMs = 0;
    state.lastTick = now();
  }
  state.owner = owner;
  state.ownerSeen = true;
  return owner;
};

const freshState = () => ({
  started: false,
  queue: [],
  sent: new Set(),
  timer: null,
  // undefined until the first check, so starting signed out counts as an
  // owner (null) whose events a later sign-in must not inherit.
  owner: undefined,
  ownerSeen: false,
  memorySession: null,
  route: null,
  visible: true,
  engagedMs: 0,
  lastTick: 0,
  lastActivity: 0,
  listeners: [],
});

let state = freshState();

const now = () => Date.now();

const send = (events, { keepalive = false } = {}) => {
  if (!events.length) return;
  try {
    const token = getAuthToken();
    fetch(`${apiBaseUrl()}${ENDPOINT_PATH}`, {
      method: 'POST',
      credentials: 'include',
      keepalive,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events }),
    }).catch(() => {});
  } catch {
    // Analytics must never break the app.
  }
};

/** Sends what is queued, in batches of at most 25. Held while signed out. */
export const flush = ({ keepalive = false } = {}) => {
  syncOwner();
  if (!state.queue.length || !isSignedIn()) return;
  const pending = state.queue;
  state.queue = [];
  for (let i = 0; i < pending.length; i += MAX_BATCH) {
    send(pending.slice(i, i + MAX_BATCH), { keepalive });
  }
};

const scheduleFlush = () => {
  if (state.timer) return;
  state.timer = setTimeout(() => {
    state.timer = null;
    flush();
    if (state.queue.length) scheduleFlush();
  }, FLUSH_INTERVAL_MS);
};

const record = (name, props, { route, autoFlush = true } = {}) => {
  try {
    if (!isEnabled()) return;
    const path = currentPath();
    if (isTokenRoute(path)) return;
    const owner = syncOwner();
    const clean = validateProps(name, props);
    if (clean === null) {
      warn('unknown event', name);
      return;
    }
    const given = Object.keys(props || {}).filter((key) => props[key] != null);
    if (given.some((key) => !(key in clean))) {
      warn('dropped properties on', name);
    }
    const { sid, seq } = nextInSession(owner);
    state.queue.push({
      event_id: uuid(),
      name,
      ts: now(),
      sid,
      seq,
      route: route || routeTemplate(path),
      surface: isEmbeddedMode() ? 'lti' : 'app',
      device: deviceClass(),
      build: buildSha(),
      props: clean,
    });
    if (state.queue.length > MAX_QUEUE) {
      state.queue.splice(0, state.queue.length - MAX_QUEUE);
    }
    if (autoFlush && state.queue.length >= FLUSH_AT) flush();
    else scheduleFlush();
  } catch {
    // Analytics must never break the app.
  }
};

/**
 * Records one event. Properties that are not on the catalogue for this event,
 * or whose values are not of the allowed type, are dropped before anything is
 * queued. Never throws.
 */
export const track = (name, props = {}) => record(name, props);

/** Like track(), but at most once per `key` for the life of the tab. */
export const trackOnce = (key, name, props) => {
  try {
    if (!isEnabled()) return;
    syncOwner();
    if (state.sent.has(key)) return;
    state.sent.add(key);
    track(name, props);
  } catch {
    // Analytics must never break the app.
  }
};

// Engaged time: visible, and with input in the last five minutes.
const account = (at = now()) => {
  if (!state.visible || !state.lastTick) {
    state.lastTick = at;
    return;
  }
  const countedUntil = Math.min(at, state.lastActivity + IDLE_MS);
  if (countedUntil > state.lastTick) {
    state.engagedMs += countedUntil - state.lastTick;
  }
  state.lastTick = at;
};

// Reported against the page the time was spent on, which on a route change is
// the previous one (the hash has already moved on).
const emitEngaged = (endedBy) => {
  account();
  const engaged = Math.min(Math.round(state.engagedMs), MAX_ENGAGED_MS);
  state.engagedMs = 0;
  if (engaged > 0 && state.route) {
    // Never flushes on its own: on page hide the caller sends with keepalive.
    record(
      'page_engaged',
      { engaged_ms: engaged, ended_by: endedBy },
      { route: state.route, autoFlush: false }
    );
  }
};

const onRouteChange = () => {
  const next = routeTemplate();
  if (next === state.route) return;
  const previous = state.route;
  if (previous) emitEngaged('route_change');
  state.route = next;
  const at = now();
  state.lastTick = at;
  state.lastActivity = at;
  track('page_viewed', previous ? { from_route: previous } : {});
};

const onActivity = () => {
  const at = now();
  if (at - state.lastActivity < ACTIVITY_THROTTLE_MS) return;
  account(at);
  state.lastActivity = at;
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    emitEngaged('hidden');
    state.visible = false;
    flush({ keepalive: true });
  } else {
    state.visible = true;
    const at = now();
    state.lastTick = at;
    state.lastActivity = at;
  }
};

const onPageHide = () => {
  if (state.visible) {
    emitEngaged('hidden');
    state.visible = false;
  }
  flush({ keepalive: true });
};

const listen = (target, type, handler, options) => {
  target.addEventListener(type, handler, options);
  state.listeners.push(() =>
    target.removeEventListener(type, handler, options)
  );
};

/**
 * Starts route and engagement tracking. Safe to call on every App render: only
 * the first call does anything. Records the first page view straight away.
 */
export const startProductEvents = () => {
  try {
    if (!isEnabled() || state.started) return;
    state.started = true;
    state.visible = document.visibilityState !== 'hidden';
    listen(window, 'hashchange', onRouteChange);
    listen(document, 'visibilitychange', onVisibilityChange);
    listen(window, 'pagehide', onPageHide);
    ['pointerdown', 'keydown', 'wheel', 'touchstart', 'mousemove'].forEach(
      (type) => listen(window, type, onActivity, { passive: true })
    );
    listen(window, 'scroll', onActivity, { passive: true, capture: true });
    onRouteChange();
  } catch {
    // Analytics must never break the app.
  }
};

/** app_opened, once per tab: 'sign_in' after a code exchange, else 'returning'. */
export const trackAppOpened = (entry) =>
  trackOnce('app_opened', 'app_opened', { entry });

/** Test-only: forget everything, including listeners. */
export const __resetProductEventsForTests = () => {
  state.listeners.forEach((remove) => remove());
  if (state.timer) clearTimeout(state.timer);
  state = freshState();
  try {
    sessionStorage.removeItem(SID_KEY);
  } catch {
    // ignore
  }
};

/** Test-only: what is waiting to be sent. */
export const __queuedEventsForTests = () => state.queue.slice();
